import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  IconProps,
  Popover,
  PopoverOptions,
  ToggleLink,
  ToggleProps,
  ToggleStandard,
  Tooltip,
} from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { clearFilterIdentity, useFilterSelector } from '../../FilterSlice';
import { FiltersProps } from '../../Filters';
import { FilterContent, IntlErrorBoundary } from '_common/components';
import { getModalContainer } from 'utils';

type FilterPopoverProps = FiltersProps & {
  popperSettings?: { distance?: number };
  placement?: PopoverOptions['placement'];
  //Exclude variant that cannot have an icon
  toggleVariant?: Exclude<ToggleProps['variant'], 'pill'>;
  toggleColor?: IconProps['color'];
  persist?: boolean;
  variant?: 'default' | 'reviewer';
};

const FilterPopover = ({
  identity,
  popperSettings,
  placement,
  toggleVariant = 'standard',
  toggleColor = 'neutral',
  persist,
  variant = 'default',
  ...props
}: FilterPopoverProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { selectAppliedFilterCount } = useFilterSelector();

  const [isOpen, setIsOpen] = useState(false);

  const appliedFilterCount = useSelector((state) =>
    selectAppliedFilterCount(state, state.filters[identity]),
  );

  useEffect(() => {
    if (!persist) {
      return () => {
        dispatch(clearFilterIdentity({ identity, noFeedback: true }));
      };
    }
  }, [persist]);

  const getIconBadge = () => {
    if (appliedFilterCount) {
      return {
        icon: 'Badge',
        size: 's',
        number: appliedFilterCount,
        position: 'top-right',
      } as const;
    }
  };

  const ToggleComponent = toggleVariant === 'link' ? ToggleLink : ToggleStandard;

  return (
    <Popover.Root
      placement={placement || 'bottom-end'}
      open={isOpen}
      onOpenChange={() => setIsOpen(!isOpen)}
      options={{
        offset: popperSettings?.distance ?? 8,
      }}
    >
      <Popover.Trigger>
        <Tooltip
          placement="bottom"
          content={intl.formatMessage({ id: 'global.filter' })}
          testId={`${identity}-filterPopover`}
        >
          <span>
            {variant === 'reviewer' ? (
              <ToggleLink
                size="m"
                isToggled={isOpen}
                iconProps={{
                  icon: 'filters',
                  badge: getIconBadge(),
                }}
                testId={`${identity}-filterPopover`}
                onClick={() => setIsOpen(!isOpen)}
                variant="neutral"
              />
            ) : (
              <ToggleComponent
                size="m"
                variant="neutral"
                isToggled={isOpen}
                iconProps={{
                  icon: appliedFilterCount ? 'regular-filters-circle-check' : 'filters',
                  color: !isOpen ? toggleColor : undefined,
                }}
                iconPrefixActive={appliedFilterCount ? 'fak' : 'fas'}
                iconPrefixInactive={appliedFilterCount ? 'fak' : 'far'}
                testId={`${identity}-filterPopover`}
                onClick={() => setIsOpen(!isOpen)}
              />
            )}
          </span>
        </Tooltip>
      </Popover.Trigger>
      <Popover.Content testId={`${identity}-filterPopover`} container={getModalContainer()}>
        <IntlErrorBoundary size="xs" style={{ margin: 'auto 2.25rem' }}>
          <FilterContent identity={identity} persist={persist} {...props} />
        </IntlErrorBoundary>
      </Popover.Content>
    </Popover.Root>
  );
};

export default FilterPopover;
